import { getAllPackages } from "../api/packageApi";
import { Package } from "../types/package";
import { CurrencyCode } from "../utils/currency";
import { getFlagUrl } from "../utils/countryFlags";

export interface OperatorModel {
  operatorId: string;
  operatorName: string;
}
export interface CountryOperator {
  countryId: string;
  flagUrl: string;
  countryName: string;
  countryCode: string;
  operators: OperatorModel[];
}
export interface PackageModel {
  packageId: string;
  price: number;
  currencies: Partial<Record<Exclude<CurrencyCode, "LAK">, number>>;
  data: string;
  minutes: string;
  packageName: string;
  duration: string;
  area: string;
  countries: CountryOperator[];
}
export interface PackageCategoryModel {
  categoryName: string;
  packages: PackageModel[];
}

const FOREIGN_CURRENCY_CODES: Exclude<CurrencyCode, "LAK">[] = [
  "USD",
  "THB",
  "EUR",
  "GBP",
  "AUD",
  "CNY",
];

const mapCurrencies = (
  list: Package["currencies"],
): PackageModel["currencies"] => {
  const out: PackageModel["currencies"] = {};
  for (const entry of list ?? []) {
    const upper = entry.code.toUpperCase() as Exclude<CurrencyCode, "LAK">;
    if (!FOREIGN_CURRENCY_CODES.includes(upper)) continue;
    const value = Number(entry.amount);
    if (Number.isFinite(value)) out[upper] = value;
  }
  return out;
};

export const fetchAllPackages = async (): Promise<PackageCategoryModel[]> => {
  const res = await getAllPackages();

  // The API omits collections such as countries/operators instead of sending
  // empty arrays, so each level is defaulted to keep one gap from failing the
  // whole catalog.
  const categories = Array.isArray(res?.data) ? res.data : [];

  const data: PackageCategoryModel[] = categories.map((category) => ({
    categoryName: category.category_name,
    packages: (category.packages ?? []).map((pkg: Package) => ({
      packageId: pkg.uuid,
      price: parseFloat(pkg.price),
      currencies: mapCurrencies(pkg.currencies),
      data: pkg.package_quota,
      minutes: pkg.voice_quota,
      packageName: pkg.package_name,
      duration: pkg.day_quota,
      area: pkg.area,
      countries: (pkg.countries ?? []).map((country) => ({
        countryId: country.country_id,
        countryName: country.country,
        countryCode: country.country_code,
        flagUrl: getFlagUrl(country.country_code),
        operators: (country.operators ?? []).map((op) => ({
          operatorId: op.operator_id,
          operatorName: op.operator_name,
        })),
      })),
    })),
  }));

  return data;
};
